// @ts-ignore — @turf/turf types work at runtime; moduleResolution issue with exports field
import * as turf from '@turf/turf'
import type { Geometry as OlGeometry } from 'ol/geom'
import GeoJSON from 'ol/format/GeoJSON'
import type { GeoJsonGeometry, GeoJsonFeature, LonLat, BboxCoordinate } from '../../types/geo'

const geoJsonFormat = new GeoJSON()

/** Wrap a raw geometry in a GeoJSON Feature */
export function toGeoJsonFeature(
  geom: GeoJsonGeometry,
  properties: Record<string, unknown> = {}
): GeoJsonFeature {
  return { type: 'Feature', geometry: geom, properties }
}

/**
 * Get centroid of any GeoJSON geometry.
 * Returns [lon, lat] — GeoJSON order.
 */
export function getCentroid(geom: GeoJsonGeometry): LonLat {
  try {
    const feature = turf.feature(geom as turf.Geometry)
    const c = turf.centroid(feature)
    const [lon, lat] = c.geometry.coordinates as [number, number] // [lon, lat] - GeoJSON order
    return [lon, lat]
  } catch {
    return [0, 0]
  }
}

/**
 * Get bounding box of a GeoJSON geometry.
 */
export function getBbox(geom: GeoJsonGeometry): BboxCoordinate {
  try {
    const feature = turf.feature(geom as turf.Geometry)
    const [minLon, minLat, maxLon, maxLat] = turf.bbox(feature) // [minLon, minLat, maxLon, maxLat]
    return { minLon, minLat, maxLon, maxLat }
  } catch {
    return { minLon: 0, minLat: 0, maxLon: 0, maxLat: 0 }
  }
}

/**
 * Get area of a polygon geometry in km².
 * Returns 0 for non-polygon types.
 */
export function getAreaKm2(geom: GeoJsonGeometry): number {
  if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') return 0
  try {
    const feature = turf.feature(geom as turf.Geometry)
    return turf.area(feature) / 1_000_000
  } catch {
    return 0
  }
}

/**
 * Convert an OpenLayers geometry to GeoJSON geometry object.
 * OL uses EPSG:4326 by default when configured with that projection.
 */
export function olGeometryToGeoJson(olGeom: OlGeometry): GeoJsonGeometry | null {
  try {
    const feature = geoJsonFormat.writeGeometryObject(olGeom, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    })
    return feature as GeoJsonGeometry
  } catch {
    return null
  }
}

/**
 * Convert a GeoJSON geometry to an OpenLayers geometry.
 */
export function geoJsonToOlGeometry(geom: GeoJsonGeometry): OlGeometry | null {
  try {
    return geoJsonFormat.readGeometry(geom, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    })
  } catch {
    return null
  }
}
